/**
 * @fileoverview The error a failed HEIC → JPEG conversion rejects with, plus
 * the helpers that turn it into Sentry context and into user-facing copy.
 *
 * Standalone like `fileReadError.ts`: `sentry.ts` and `apiErrors.ts` consume
 * it, and neither may pull in the image upload graph.
 */

/**
 * Why a HEIC file did not become a JPEG.
 *
 * - `decoder_load`: the self-hosted `/vendor/heic-to.js` module did not import.
 * - `worker`: the decoder's Web Worker died. heic-to runs libheif in a worker
 *   built from a `blob:` URL, which a `worker-src` policy without `blob:` (an
 *   extension, an enterprise CSP) blocks; heic-to never replaces a dead worker,
 *   so only a reload brings the decoder back.
 * - `decode`: the worker answered with an error — a truncated or mislabeled file.
 * - `timeout`: the decode never answered (see `HEIC_DECODE_TIMEOUT_MS`).
 */
export type HeicDecodeFailure = 'decoder_load' | 'worker' | 'decode' | 'timeout'

/**
 * `message` carries only the failure kind so the Sentry title stays low
 * cardinality; the file's identity and the decoder's own words ride on the
 * instance and go to Sentry as context, never to the UI.
 */
export class HeicDecodeError extends Error {
  readonly failure: HeicDecodeFailure
  readonly fileName: string
  readonly fileSize: number
  /** The File's MIME type, or `''` when the browser reported none. */
  readonly fileType: string
  /** What the decoder rejected with, as text; `''` when it rejected with nothing. */
  readonly detail: string
  /**
   * Set by `convertHeicFile`, the one place that reports the failure, so the
   * surfaces that catch it afterwards do not each report it again.
   */
  reportedToSentry = false

  constructor(failure: HeicDecodeFailure, file: File, cause?: unknown) {
    super(`HEIC decode failed (${failure})`)
    this.name = 'HeicDecodeError'
    this.failure = failure
    this.fileName = file.name
    this.fileSize = file.size
    this.fileType = file.type
    this.detail = describeCause(cause)
    if (cause !== undefined) this.cause = cause
  }
}

function describeCause(cause: unknown): string {
  if (cause === undefined || cause === null) return ''
  if (cause instanceof Error) return cause.message
  return typeof cause === 'string' ? cause : String(cause)
}

export function isHeicDecodeError(error: unknown): error is HeicDecodeError {
  return error instanceof HeicDecodeError
}

/**
 * The diagnostic payload a Sentry event needs to explain the failure without a
 * session replay. Kept off `message` so it can't fragment issue grouping.
 */
export function heicDecodeErrorContext(error: HeicDecodeError): Record<string, unknown> {
  return {
    file_name: error.fileName,
    file_size: error.fileSize,
    file_type: error.fileType || '(none)',
    decoder_detail: error.detail || '(none)',
  }
}

/** One Sentry issue per failure kind, whichever upload surface hit it. */
export function heicDecodeErrorFingerprint(error: HeicDecodeError): string[] {
  return ['heic-decode-error', error.failure]
}

/**
 * The line the upload tray shows for a HEIC that could not be converted. Names
 * the file, says what stood in the way when that is known, and always offers
 * the one exit every failure kind shares: a JPEG export.
 */
export function describeHeicDecodeFailure(fileName: string, error: HeicDecodeError): string {
  const lead = `"${fileName}" could not be converted`
  switch (error.failure) {
    case 'decoder_load':
      return `${lead} — the HEIC decoder did not load. Try again, or export it as JPEG.`
    case 'worker':
      return `${lead} — this browser blocks the HEIC decoder. Export it as JPEG and try again.`
    case 'timeout':
      return `${lead} — the decoder did not finish. Export it as JPEG and try again.`
    case 'decode':
      return `${lead}. Export it as JPEG and try again.`
  }
}
